// src/DashboardMetrics.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Avatar,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import BusinessTrends from './BusinessTrends';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FlagIcon from '@mui/icons-material/Flag';
import GavelIcon from '@mui/icons-material/Gavel';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { brand, ink, surface, type as typeScale } from './designTokens';
import DepthCard from './DepthCard';
import useAdminNotifications from './useAdminNotifications';
// Single metric card.
//
// Previously hovered to MUI's default blue with blue icons — the loudest
// off-brand element on the first screen an admin sees. Now the figure is the
// hero (large, tabular, ink) with the label and icon deliberately quiet, and
// the brand gold reserved for the hover state as the only accent.
const MetricCard = ({ title, value, icon, to }) => {
  const navigate = useNavigate();
  // A number with nowhere to go is a dead end: seeing "7 failed payments" and
  // then having to find the bookings page by hand is the slow part of the job.
  // DepthCard already gives an onClick the button role, a tab stop and Enter /
  // Space, so the whole card is reachable from the keyboard too.
  const go = to ? () => navigate(to) : undefined;
  return (
    <DepthCard onClick={go} sx={to ? { cursor: 'pointer' } : {}}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={{ color: ink[500], fontSize: typeScale.micro, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {title}
        </Typography>
        <Box sx={{ color: brand.gold, display: 'flex' }}>{icon}</Box>
      </Box>
      <Typography
        sx={{
          fontSize: typeScale.display,
          fontWeight: 500,
          color: ink[900],
          lineHeight: 1.1,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </DepthCard>
  );
};

export default function DashboardMetrics({
  appUsageHeight = 375,
  profilesHeight = 375,
}) {
  const navigate = useNavigate();
  // Shared with the header bell so the two can never disagree.
  const notifications = useAdminNotifications();
  const [metrics, setMetrics] = useState({
    openTickets: 0,
    membersCount: 0,
    newSignUps: 0,
    pendingBookings: 0,
    pendingPayouts: 0,
    criticalErrors: 0,
    warnings: 0,
    openReports: 0,
    disputes: 0,
    failedPayments: 0,
    failedPayouts: 0,
    applications: 0,
    netRevenueCents: 0,
  });
  const [recentProfiles, setRecentProfiles] = useState([]);
  const [usageData, setUsageData] = useState([]);

  const buildPublicUrl = useCallback((bucket, path) => {
    if (!path) return null;
    const trimmed = path.replace(/^https?:\/\/[^/]+\/storage\/v1\/object\/public\//, '');
    if (trimmed.startsWith('http')) return trimmed;
    const clean = trimmed.replace(/^\/+/, '');
    const base = process.env.REACT_APP_SUPABASE_URL;
    return `${base}/storage/v1/object/public/${bucket}/${clean}`;
  }, []);

  const normalizeProfileImage = useCallback((img) => {
    if (!img) return null;
    if (img.startsWith('http')) return img;
    const clean = img.startsWith('profiles/') ? img : `profiles/${img}`;
    return buildPublicUrl('profile-images', clean);
  }, [buildPublicUrl]);

  const getWeek = useCallback((date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  }, []);

  const buildUsageSeries = useCallback((list = []) => {
    const counts = new Map();
    list.forEach(item => {
      const d = new Date(item.created_at || item.booking_date || item.updated_at);
      if (isNaN(d)) return;
      const key = `${d.getFullYear()}-W${getWeek(d)}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const series = Array.from(counts.entries())
      .map(([week, users]) => ({ week, users }))
      .sort((a, b) => (a.week > b.week ? 1 : -1));
    return series.slice(-12); // last 12 weeks
  }, [getWeek]);

  useEffect(() => {
    console.log('DashboardMetrics: Component mounted, fetching data...');
    // fetch all counts
    const fetchAll = async () => {
      try {
        console.log('DashboardMetrics: Starting fetchAll...');
        // Open Tickets
        const { data: tickets } = await supabase
          .from('Contact Us').select('*').eq('status', 'Open');
        const openTickets = tickets?.length || 0;

        // Total Members - count all profiles
        // Try with service role key if authenticated fails
        let membersCount = 0;
        let membersError = null;
        try {
          const result = await supabase
            .from('Profiles')
            .select('*', { count: 'exact', head: true });
          membersCount = result.count || 0;
          membersError = result.error;
        } catch (err) {
          console.error('Error fetching members count:', err);
          membersError = err;
        }
        
        if (membersError) {
          console.error('Error fetching members count:', membersError);
          // Try alternative: fetch all and count
          try {
            const { data } = await supabase.from('Profiles').select('id');
            membersCount = data?.length || 0;
          } catch (e) {
            console.error('Alternative count also failed:', e);
          }
        }

        // New Sign-Ups - users signed up within the current month
        const now = new Date();
        const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        let newSignUps = 0;
        let signUpsError = null;
        try {
          const result = await supabase
            .from('Profiles')
            .select('*', { count: 'exact', head: true })
            .gte('created_at', firstDayOfMonth.toISOString());
          newSignUps = result.count || 0;
          signUpsError = result.error;
        } catch (err) {
          console.error('Error fetching new sign-ups:', err);
          signUpsError = err;
        }
        
        if (signUpsError) {
          console.error('Error fetching new sign-ups:', signUpsError);
          // Try alternative: fetch all and filter
          try {
            const { data } = await supabase
              .from('Profiles')
              .select('created_at')
              .gte('created_at', firstDayOfMonth.toISOString());
            newSignUps = data?.length || 0;
          } catch (e) {
            console.error('Alternative sign-ups count also failed:', e);
          }
        }

        // Pending Bookings - any bookings that are NOT marked as 'Completed'
        const { data: bookings, error: bookingsError } = await supabase
          .from('bookings')
          .select('*')
          .neq('status', 'Completed');
        const pendingBookings = bookings?.length || 0;
        const usageSeries = buildUsageSeries(bookings || []);
        
        if (bookingsError) console.error('Error fetching pending bookings:', bookingsError);

        // Pending Payouts - any payouts with status 'pending'
        const { data: payouts, error: payoutsError } = await supabase
          .from('payout_requests')
          .select('*')
          .eq('status', 'pending');
        const pendingPayouts = payouts?.length || 0;
        
        if (payoutsError) console.error('Error fetching pending payouts:', payoutsError);

        // Track errors and warnings - also log to database
        let criticalErrors = 0;
        let warnings = 0;

        // Write one row to system_errors.
        //
        // This used to be `.insert([...]).catch(...)`, which threw
        // "insert(...).catch is not a function" every single time it ran:
        // insert() returns a thenable with `then` but no `catch`. It only runs
        // when a query has already failed, so the one moment the log mattered
        // was the one moment it crashed — and nothing was recorded. That is
        // part of why the error log looked empty.
        //
        // Two rules here now: never throw out of the logger, and never let a
        // repeated failure flood the table. The dashboard refetches on every
        // mount, so an outage lasting an hour would otherwise write hundreds of
        // identical rows and bury everything else on the Issues page.
        const logSystemError = async (row) => {
          try {
            const since = new Date(Date.now() - 10 * 60 * 1000).toISOString();
            const { data: recent } = await supabase
              .from('system_errors')
              .select('id')
              .eq('title', row.title)
              .gte('created_at', since)
              .limit(1);
            if (recent && recent.length > 0) return;

            const { error: logError } = await supabase
              .from('system_errors')
              .insert([{ source: 'dashboard', ...row }]);
            if (logError) console.error('Failed to log error:', logError);
          } catch (err) {
            console.error('Failed to log error:', err);
          }
        };

        // Check for database errors and log to system_errors table
        if (membersError) {
          criticalErrors++;
          await logSystemError({
            error_type: 'database',
            severity: 'error',
            title: 'Profiles Query Failed',
            message: `Profiles query failed: ${membersError.message}`,
            details: { error: membersError },
          });
        }
        if (bookingsError) {
          criticalErrors++;
          await logSystemError({
            error_type: 'database',
            severity: 'error',
            title: 'Bookings Query Failed',
            message: `Bookings query failed: ${bookingsError.message}`,
            details: { error: bookingsError },
          });
        }
        if (payoutsError) {
          warnings++;
          await logSystemError({
            error_type: 'database',
            severity: 'warning',
            title: 'Payouts Query Failed',
            message: `Payouts query failed: ${payoutsError.message}`,
            details: { error: payoutsError },
          });
        }

        // Check for data inconsistencies
        if (pendingBookings > 100) {
          warnings++;
          await logSystemError({
            error_type: 'data_inconsistency',
            severity: 'warning',
            title: 'High Pending Bookings',
            message: `High number of pending bookings: ${pendingBookings}`,
            details: { count: pendingBookings },
          });
        }
        if (openTickets > 50) {
          warnings++;
          await logSystemError({
            error_type: 'data_inconsistency',
            severity: 'warning',
            title: 'High Open Tickets',
            message: `High number of open tickets: ${openTickets}`,
            details: { count: openTickets },
          });
        }

        console.log('DashboardMetrics: Metrics fetched successfully:', {
          openTickets, 
          membersCount: membersCount || 0, 
          newSignUps: newSignUps || 0, 
          pendingBookings, 
          pendingPayouts,
          criticalErrors,
          warnings,
        });
        // The tiles audit item s72-3-4 asks for. Each is counted where the
        // thing actually lives rather than inferred from a status elsewhere.
        let openReports = 0, disputes = 0, failedPayments = 0,
            failedPayouts = 0, applications = 0, netRevenueCents = 0;
        try {
          const [reportsRes, disputeRes, failedPayRes, failedPayoutRes, appsRes, ledgerRes] =
            await Promise.all([
              supabase.from('content_reports').select('id', { count: 'exact', head: true }).is('handled_at', null),
              supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('status', 'Disputed'),
              supabase.from('bookings').select('id', { count: 'exact', head: true }).eq('status', 'Payment Failed'),
              // Only 'rejected' and 'cancelled' exist on payout_requests; 'failed' and
              // 'declined' are not allowed values, so two thirds of the old filter
              // could never match anything.
              supabase.from('payout_requests').select('id', { count: 'exact', head: true }).in('status', ['rejected', 'cancelled']),
              supabase.from('Profiles').select('id', { count: 'exact', head: true }).eq('stylist_status', 'submitted'),
              // Net revenue is what LIVLIFE kept: commission less Stripe's cut.
              // Commission can be negative now that a discount is absorbed
              // rather than taken from the stylist, so this can fall.
              supabase.from('booking_ledger_entries').select('kind, amount_cents'),
            ]);
          openReports = reportsRes.count || 0;
          disputes = disputeRes.count || 0;
          failedPayments = failedPayRes.count || 0;
          failedPayouts = failedPayoutRes.count || 0;
          applications = appsRes.count || 0;
          const entries = ledgerRes.data || [];
          const sumOf = (kind) => entries
            .filter((e) => e.kind === kind)
            .reduce((total, e) => total + Number(e.amount_cents || 0), 0);
          netRevenueCents = sumOf('commission') - sumOf('processing_fee');
        } catch (tileErr) {
          console.warn('Could not load the operational tiles:', tileErr);
        }

        setMetrics({ 
          openReports,
          disputes,
          failedPayments,
          failedPayouts,
          applications,
          netRevenueCents,
          openTickets, 
          membersCount: membersCount || 0, 
          newSignUps: newSignUps || 0, 
          pendingBookings, 
          pendingPayouts,
          criticalErrors,
          warnings,
        });

        // Set usage data (real bookings over last 12 weeks), fallback to a flat line if none
        if (usageSeries.length > 0) {
          setUsageData(usageSeries);
        } else {
          const weeks = ['W1','W2','W3','W4','W5','W6','W7','W8','W9','W10','W11','W12'];
          setUsageData(weeks.map(w => ({ week: w, users: 0 })));
        }
      } catch (err) {
        console.error('DashboardMetrics: Error fetching metrics:', err);
      }
    };

    // fetch profiles preview with images
    const fetchProfilesPreview = async () => {
      try {
        console.log('DashboardMetrics: Fetching profiles preview...');
        const { data, error } = await supabase
          .from('Profiles')
          .select('id, first_name, last_name, user_type, phone_number, email, profile_image, media, identity_verified, created_at')
          .order('created_at', { ascending: false })
          .limit(10);
        
        if (error) {
          console.error('DashboardMetrics: Error fetching profiles:', error);
          console.error('DashboardMetrics: This is likely an RLS policy issue. Run FIX_PROFILES_ACCESS_NOW.sql in Supabase.');
          setRecentProfiles([]);
          return;
        }
        
        if (data && data.length > 0) {
          const profilesWithImages = data.map(profile => {
            const media = (() => {
              if (!profile.media) return [];
              try {
                const arr = Array.isArray(profile.media)
                  ? profile.media
                  : JSON.parse(profile.media);
                return (arr || []).filter(Boolean);
              } catch {
                return [];
              }
            })();

            const firstMedia = media.length > 0 ? media[0] : null;
            const firstMediaString = (() => {
              if (!firstMedia) return null;
              if (typeof firstMedia === 'string') return firstMedia;
              if (typeof firstMedia === 'object') {
                // common shapes: { url }, { publicUrl }, { path }
                return firstMedia.url || firstMedia.publicUrl || firstMedia.path || null;
              }
              return null;
            })();

            const fallbackMedia = firstMediaString
              ? (firstMediaString.startsWith('http') ? firstMediaString : normalizeProfileImage(firstMediaString))
              : null;

            const imageUrl =
              normalizeProfileImage(profile.profile_image) ||
              fallbackMedia ||
              null;

            return {
              ...profile,
              imageUrl,
              // keep existing field name used by Dashboard UI
              profile_image_url: imageUrl,
            };
          });
          console.log('DashboardMetrics: Profiles fetched:', profilesWithImages.length);
          setRecentProfiles(profilesWithImages);
        } else {
          console.log('DashboardMetrics: No profiles found');
        }
      } catch (err) {
        console.error('DashboardMetrics: Error fetching profiles preview:', err);
      }
    };

    fetchAll();
    fetchProfilesPreview();
    
  }, [buildUsageSeries, normalizeProfileImage, buildPublicUrl]);

  const handleProfileClick = (profileId) => {
    navigate(`/dashboard/profile/${profileId}`);
  };

  console.log('DashboardMetrics: Rendering with metrics:', metrics, 'profiles:', recentProfiles.length);

  return (
    <Box sx={{ p: 5, overflowY: 'auto', height: '100%', bgcolor: surface.sunken }}>
      {/* KPI Cards - 5 in a row */}
      {/* What needs attention, and what the platform actually kept.
          Audit s72-3-4 — each counted at its source. */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard title="Open Reports" value={metrics.openReports} to="/dashboard/reports-queue" icon={<FlagIcon sx={{ fontSize: 24 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard title="Disputes" value={metrics.disputes} to="/dashboard/disputes" icon={<GavelIcon sx={{ fontSize: 24 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard title="Failed Payments" value={metrics.failedPayments} to="/dashboard/bookings" icon={<CreditCardOffIcon sx={{ fontSize: 24 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard title="Failed Payouts" value={metrics.failedPayouts} to="/dashboard/payouts" icon={<MoneyOffIcon sx={{ fontSize: 24 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard title="Applications" value={metrics.applications} to="/dashboard/stylist-approval" icon={<HowToRegIcon sx={{ fontSize: 24 }} />} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <MetricCard
            title="Net Revenue"
            to="/dashboard/business-metrics"
            value={`$${(metrics.netRevenueCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            icon={<TrendingUpIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Open Tickets"
            to="/dashboard/contact"
            value={metrics.openTickets}
            icon={<ConfirmationNumberIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Active Members"
            to="/dashboard/profiles"
            value={metrics.membersCount.toLocaleString()}
            icon={<PeopleIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="New Sign-Ups"
            to="/dashboard/profiles"
            value={metrics.newSignUps}
            icon={<PersonAddIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Pending Bookings"
            to="/dashboard/bookings"
            value={metrics.pendingBookings}
            icon={<EventAvailableIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Pending Payouts"
            to="/dashboard/payouts"
            value={metrics.pendingPayouts}
            icon={<AccountBalanceWalletIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
      </Grid>

      {/* User Usage Chart and Recent Profiles */}
      <BusinessTrends />

      <Grid container spacing={3}>
        {/* User Usage Chart - 2/3 width */}
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: appUsageHeight,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              bgcolor: '#fff',
              border: `1px solid ${surface.line}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: ink[900] }}>
                User Usage
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <select 
                  style={{
                    backgroundColor: surface.sunken,
                    border: `1px solid ${surface.line}`,
                    borderRadius: '4px',
                    padding: '6px 12px',
                    fontSize: '0.875rem',
                    color: ink[900],
                    cursor: 'pointer',
                  }}
                >
                  <option>Last 30 Days</option>
                  <option>Last 90 Days</option>
                  <option>Last 6 Months</option>
                </select>
              </Box>
            </Box>
            <Box sx={{ flex: 1, position: 'relative' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={usageData}>
                  <defs>
                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={brand.gold} stopOpacity={0.1}/>
                      <stop offset="95%" stopColor={brand.gold} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis />
                  <Tooltip />
                  <Area 
                    type="monotone" 
                    dataKey="users" 
                    stroke={brand.gold} 
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorUsers)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Profiles - 1/3 width */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              height: profilesHeight,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              bgcolor: '#fff',
              border: `1px solid ${surface.line}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: ink[900] }}>
                Recent Profiles
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/dashboard/profiles')}
                sx={{ 
                  color: brand.gold, 
                  fontWeight: 600, 
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                View All
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {recentProfiles.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', mt: 4 }}>
                  No profiles found
                </Typography>
              ) : (
                recentProfiles.map((profile) => (
                  <Box
                    key={profile.id}
                    onClick={() => handleProfileClick(profile.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      bgcolor: surface.sunken,
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: brand.goldWash,
                        transform: 'translateX(4px)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                      <Box sx={{ position: 'relative', width: 40, height: 40 }}>
                        <Avatar
                          src={profile.profile_image_url}
                          sx={{ width: 40, height: 40 }}
                        >
                          {profile.first_name?.[0] || profile.last_name?.[0] || '?'}
                        </Avatar>
                        {(profile.identity_verified === true || profile.identity_verified === 'true' || profile.identity_verified === 1) && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: -2,
                              right: -2,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: surface.card,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `1px solid ${surface.line}`
                            }}
                            title="Verified"
                          >
                            <Box component="span" sx={{ fontSize: 12, lineHeight: 1, color: brand.goldDeep }}>✓</Box>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 600, 
                            color: ink[900],
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {profile.first_name} {profile.last_name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: ink[500],
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {profile.user_type || 'Client'}
                        </Typography>
                      </Box>
                    </Box>
                    <ChevronRightIcon sx={{ color: '#999', fontSize: 20 }} />
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Needs Attention - full width, below the row above.
            Full width because it is a list of sentences rather than numbers,
            and because putting it beside the chart pushed Recent Profiles onto
            a row of its own and made the page taller than a screen.
            The same list the header bell shows, from the same hook: two
            components running their own queries would disagree about the count
            the moment either gained a source. */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              minHeight: 180,
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              bgcolor: '#fff',
              border: `1px solid ${surface.line}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: ink[900] }}>
                Needs Attention
                {notifications.unreadCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.25,
                      borderRadius: 10,
                      bgcolor: 'error.main',
                      color: '#fff',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                    }}
                  >
                    {notifications.unreadCount}
                  </Box>
                )}
              </Typography>
              {notifications.unreadCount > 0 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={notifications.markAllAsRead}
                  sx={{
                    color: brand.gold,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Clear all
                </Button>
              )}
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {notifications.unread.length === 0 ? (
                <Typography variant="body2" sx={{ color: ink[500], textAlign: 'center', mt: 4 }}>
                  Nothing waiting. You're all caught up.
                </Typography>
              ) : (
                notifications.unread.map((n) => (
                  <Box
                    key={n.id}
                    onClick={async () => {
                      await notifications.markAsRead(n.id);
                      if (n.action) navigate(n.action);
                    }}
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      border: `1px solid ${surface.line}`,
                      '&:hover': { bgcolor: surface.page },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        mt: 0.75,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor:
                          n.type === 'error'
                            ? 'error.main'
                            : n.type === 'warning'
                            ? 'warning.main'
                            : 'info.main',
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: ink[900] }}>
                        {n.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: ink[500],
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {n.message}
                      </Typography>
                    </Box>
                  </Box>
                ))
              )}
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
}
